import { defineMessages } from '@alltrails/shared/react-intl';
import AppStore from '@alltrails/shared/components/storeBadges/AppStore';
import GooglePlay from '@alltrails/shared/components/storeBadges/GooglePlay';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import starRating from '../ExploreAndDiscover/star_rating.png';
import { toATLocalString } from '../../../utils/number_util';
import * as styles from './styles/styles.module.scss';
import { MEDIA_QUERY_SMALL_DOWN } from '../../../utils/constants/breakpoints';

const EDITORS_CHOICE_STRINGS = defineMessages({
  FIND_YOUR_WAY_OUTSIDE: {
    defaultMessage: 'Find your way outside'
  },
  EDITORS_CHOICE: {
    defaultMessage: 'Recipient of the App Store Editors’ Choice award'
  },
  BG_IMAGE_ALT: {
    defaultMessage: 'People hiking down a grassy hill'
  },
  RATING_IMAGE_ALT: {
    defaultMessage: '4.9 stars out of 5'
  }
});

const BG_IMAGE_BASE_URL = 'https://cdn-assets.alltrails.com/assets/images/homepage/editors_choice';

const isEnglish = (languageRegionCode: LanguageRegionCode): boolean => {
  const englishLanguages: LanguageRegionCode[] = [LanguageRegionCode.English, LanguageRegionCode.EnglishGB];
  return englishLanguages.includes(languageRegionCode);
};

const EditorsChoiceBlock = (): JSX.Element => {
  const languageRegionCode = useLanguageRegionCode();
  const {
    formattedDefaultMessages: { FIND_YOUR_WAY_OUTSIDE, EDITORS_CHOICE, BG_IMAGE_ALT, RATING_IMAGE_ALT }
  } = useFormatMessage(EDITORS_CHOICE_STRINGS);

  const storeHeight = 46;

  const desktopBgImageUrl = `${BG_IMAGE_BASE_URL}/rebrand-background-desktop`;
  const mobileBgImageUrl = `${BG_IMAGE_BASE_URL}/rebrand-background-mobile`;

  return (
    <div className={styles.container}>
      <picture className={styles.picture}>
        <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={`${mobileBgImageUrl}.avif`} type="image/avif" />
        <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={`${mobileBgImageUrl}.webp`} type="image/webp" />
        <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={`${mobileBgImageUrl}.jpg`} type="image/jpg" />
        <source srcSet={`${desktopBgImageUrl}.avif`} type="image/avif" />
        <source srcSet={`${desktopBgImageUrl}.webp`} type="image/webp" />
        <img src={`${desktopBgImageUrl}.jpg`} alt={BG_IMAGE_ALT} className={styles.containerBgImage} loading="lazy" />
      </picture>
      <div className={styles.innerContainer}>
        <div className={styles.badgeStarContainer}>
          <img
            className={styles.badgeImage}
            src={`https://cdn-assets.alltrails.com/assets/images/homepage/editors_choice/rebrand-apple-editor-badge-${languageRegionCode}.svg`}
            alt={EDITORS_CHOICE}
            loading="lazy"
          />
          <span className={styles.rating}>{toATLocalString(4.9, languageRegionCode, 1)}</span>
          <img alt={RATING_IMAGE_ALT} src={starRating} className={styles.starsImage} loading="lazy" />
        </div>
        <h3 className={styles.titleTop}>
          {FIND_YOUR_WAY_OUTSIDE}
          {isEnglish(languageRegionCode) && <span className={styles.trademark}>&#8482;</span>}
        </h3>
        <div className={styles.stores}>
          <AppStore height={storeHeight} languageRegionCode={languageRegionCode} />
          <GooglePlay height={storeHeight} languageRegionCode={languageRegionCode} />
        </div>
      </div>
    </div>
  );
};

export default EditorsChoiceBlock;
